'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Book Now', href: '/book-appointment' },
  { label: 'Subscription', href: '/subscription' },
  { label: 'Loyalty', href: '/loyalty' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

export default function PublicNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-sky-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={36} />
            <span className={`font-extrabold text-lg tracking-tight hidden sm:block ${scrolled ? 'text-dark' : 'text-white'}`}>
              Drip N Dry
              <span className="text-primary"> Curaçao</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.label}`}
                href={link?.href}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 ${
                  scrolled
                    ? 'text-slate-700 hover:text-primary hover:bg-sky-50' : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/sign-up-login-screen"
              className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-150 ${
                scrolled
                  ? 'border-primary text-primary hover:bg-sky-50' : 'border-white/60 text-white hover:bg-white/10'
              }`}
            >
              Sign In
            </Link>
            <Link href="/book-appointment" className="btn-primary text-sm px-5 py-2.5">
              Book Now
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-dark' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-sky-100 shadow-xl">
          <div className="px-4 py-4 space-y-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm font-semibold text-slate-700 hover:text-primary hover:bg-sky-50 transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-sky-100 flex flex-col gap-2">
              <Link
                href="/sign-up-login-screen"
                onClick={() => setMobileOpen(false)}
                className="btn-outline text-sm py-2.5 justify-center"
              >
                Sign In / Register
              </Link>
              <Link
                href="/book-appointment"
                onClick={() => setMobileOpen(false)}
                className="btn-primary text-sm py-2.5 justify-center"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}